<!-- src\views\components\PlayList.vue -->
<template>
  <div class="card mb-4">

    <!-- CARD HEADER -->
    <div class="card-header pb-0 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
      <h6 class="mb-0">Play List</h6>
      <div class="d-flex align-items-center w-100 w-md-auto justify-content-between justify-content-md-end flex-wrap gap-2">
        <div class="btn-group btn-group-sm me-md-3 shadow-sm">
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'table' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'table'"
          >
            <i class="fas fa-table"></i>
          </button>
          <button 
            class="btn btn-sm mb-0" 
            :class="viewMode === 'card' ? 'btn-primary text-white' : 'btn-outline-primary'" 
            @click="viewMode = 'card'"
          >
            <i class="fas fa-th-large"></i>
          </button>
        </div>

        <button type="button" class="btn btn-theme-primary mb-0 flex-grow-1 flex-md-grow-0" @click="navigateToAddPlaylist()">
          ADD Playlist
        </button>
      </div>
    </div>

    <div class="card-body px-0 pt-0 pb-2">
      
      <!-- 1. TABLE VIEW -->
      <div v-if="viewMode === 'table'" class="table-responsive p-0">
        <table class="table align-items-center mb-0">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 text-left">Play list name</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">Emergency</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">{{ $t('status') }}</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-left">{{ $t('action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(role, index) in paginatedRoles" :key="index">
              <td class="text-left">
               <p class="text-xs font-weight-bold mb-0 ps-3 text-dark">{{ role.playlist_name }}</p>
              </td>
              <td class="text-left">
                <span class="text-xs font-weight-bold" :class="role.is_emergency_broadcast == 1 ? 'text-danger' : 'text-secondary'">
                   <i v-if="role.is_emergency_broadcast == 1" class="fas fa-exclamation-triangle me-1 text-danger"></i>
                   {{ role.is_emergency_broadcast == 1 ? 'Yes' : 'No' }}
                </span>
              </td>
              <td class="text-left">
                <soft-badge :color="role.status == 1 ? 'success' : 'secondary'" variant="gradient" size="sm">{{
                  role.status == 1 ? 'Active' : "InActive" }}</soft-badge>
              </td>
              <td class="text-left">
                <a href="javascript:;" @click="navigateToEditPlaylist(role.id)" class="text-secondary font-weight-bold text-xs me-3">
                  <i class="fas fa-edit"></i>
                </a>
                <!-- NEW: History Action for Table -->
                <a href="javascript:;" @click="openHistoryModal(role)" class="text-info font-weight-bold text-xs">
                  <i class="fas fa-history"></i>
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 2. CARD VIEW (As seen in your screenshot) -->
      <div v-else class="px-3 px-md-4 pb-4 pt-3">
        <div class="row">
          <div v-for="(role, index) in paginatedRoles" :key="'card-' + index" class="col-12 col-md-6 col-xl-3 mb-4">
            <div class="card h-100 border shadow-sm">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <div class="d-flex align-items-center overflow-hidden">
                    <div class="icon-shape icon-sm shadow text-center border-radius-md d-flex align-items-center justify-content-center me-2 theme-icon-bg flex-shrink-0" style="width: 32px; height: 32px;">
                      <i class="fas fa-list-ul text-white text-xs opacity-10"></i>
                    </div>
                   <h6 class="mb-0 text-sm text-dark text-truncate">{{ role.playlist_name }}</h6>
                  </div>
                  <div class="d-flex gap-2">
                    <!-- NEW: History Icon -->
                    <a href="javascript:;" @click="openHistoryModal(role)" class="text-info" title="View History">
                      <i class="fas fa-history text-xs"></i>
                    </a>
                    <a href="javascript:;" @click="navigateToEditPlaylist(role.id)" class="text-secondary" title="Edit">
                      <i class="fas fa-edit text-xs"></i>
                    </a>
                  </div>
                </div>
                
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span class="text-xxs text-uppercase text-secondary font-weight-bolder">Emergency</span>
                  <span class="text-xs font-weight-bold" :class="role.is_emergency_broadcast == 1 ? 'text-danger' : 'text-secondary'">
                    <i v-if="role.is_emergency_broadcast == 1" class="fas fa-exclamation-triangle me-1"></i>
                    {{ role.is_emergency_broadcast == 1 ? 'Yes' : 'No' }}
                  </span>
                </div>

                <div class="d-flex justify-content-between align-items-center pt-3 border-top">
                  <span class="text-xxs text-uppercase text-secondary font-weight-bolder">{{ $t('status') }}</span>
                  <soft-badge :color="role.status == 1 ? 'success' : 'secondary'" variant="gradient" size="sm">
                    {{ role.status == 1 ? 'Active' : "InActive" }}
                  </soft-badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results -->
      <div v-if="roles.length === 0" class="text-center py-4 text-secondary text-sm">
        No playlists found.
      </div>

      <!-- Pagination -->
      <div class="pagination flex-wrap">
        <button @click="prevPage" :disabled="currentPage === 1">{{ $t('previous') }}</button>
        <button v-for="page in totalPages" :key="page" @click="goToPage(page)"
          :class="{ active: currentPage === page }">
          {{ page }}
        </button>
        <button @click="nextPage" :disabled="currentPage === totalPages">{{ $t('next') }}</button>
      </div>
    </div>

    <!-- NEW: HISTORY MODAL POPUP (UPDATED FOR BETTER SPACING) -->
    <div v-if="showHistory" class="modal-overlay">
      <div class="modal-container card shadow-lg animate__animated animate__fadeInDown">
        <div class="card-header bg-light d-flex justify-content-between align-items-center border-bottom py-3">
          <h6 class="mb-0 text-md"><i class="fas fa-history me-2 text-primary"></i> Audit Trail: {{ selectedPlaylistName }}</h6>
          <button class="btn-close-custom" @click="showHistory = false">&times;</button>
        </div>
        <div class="card-body p-0" style="max-height: 500px; overflow-y: auto;">
          <div v-if="!currentHistory || currentHistory.length === 0" class="p-4 text-center text-secondary">
            No update history available for this playlist.
          </div>
          <table v-else class="table align-items-center mb-0">
            <thead class="bg-gray-100">
              <tr>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-4">Date</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">User</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Action</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Changes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(log, idx) in currentHistory" :key="idx" class="border-bottom">
                <td class="align-middle ps-4 py-3">
                  <span class="text-xs font-weight-bold text-dark">{{ formatDate(log.updated_at) }}</span>
                </td>
                <td class="align-middle">
                  <p class="text-xs font-weight-bold mb-0 text-dark">{{ log.user_name }}</p>
                </td>
                <td class="align-middle">
                  <span class="badge badge-sm" :class="log.action === 'CREATED' ? 'bg-gradient-success' : 'bg-gradient-info'">
                    {{ log.action }}
                  </span>
                </td>
                <td class="align-middle py-3">
                  <!-- EACH CHANGE ITEM HAS BETTER SPACING -->
                  <div v-for="(change, field) in log.changes" :key="field" class="change-row mb-2">
                    <span class="text-xs font-weight-bolder text-dark d-block mb-1">{{ field }}:</span> 
                    <div class="d-flex align-items-center flex-wrap">
                      <span class="text-xs text-danger text-decoration-line-through bg-light px-2 py-1 rounded">{{ change.old || 'N/A' }}</span>
                      <i class="fas fa-long-arrow-alt-right mx-2 text-secondary opacity-7"></i>
                      <span class="text-xs text-success font-weight-bold bg-light px-2 py-1 rounded">
                        {{ change.new === 'Incremented' ? (parseInt(change.old || 0) + 1) : change.new }}
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="card-footer py-3 text-end border-top bg-light">
          <button class="btn btn-sm btn-dark mb-0 px-4" @click="showHistory = false">CLOSE</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import resourcePhotoUrl from "@/assets/img/resource.png";
import SoftBadge from "@/components/SoftBadge.vue";
import '@fortawesome/fontawesome-free/css/all.css';
import PlayListDataService from "../../services/PlayListDataService.js";

export default {
  name: "PlayList",
  components: {
    SoftBadge,
  },
  data() {
    return {
      viewMode: 'card', 
      isLoading: false,
      roles: [],
      users: [],
      resourcePhotoUrl,
      selectedIndex: null,
      isAddModalVisible: false,
      isEditModalVisible: false,
      selectedRoles: [], 
      currentPage: 1,
      itemsPerPage: 10,
      selectedLanguage: this.$i18n.locale,
      isImageSelected: true,
      isVideoSelected: false,
      errors: {},

      // NEW HISTORY MODAL STATE
      showHistory: false,
      currentHistory: [],
      selectedPlaylistName: ''
    };
  },
computed: {
  filteredRoles() {
    return this.roles.filter(role => role.status == 1 || role.status == '1');
  },
  totalPages() {
    return Math.ceil(this.filteredRoles.length / this.itemsPerPage);
  },
  paginatedRoles() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.filteredRoles.slice(start, end);
  },
},
  created() {
    this.getAllPlaylist();
  },
  methods: {
    // NEW: Function to prepare and show History
    openHistoryModal(playlist) {
        this.selectedPlaylistName = playlist.playlist_name;
        let history = playlist.update_history;
        
        // Handle JSON parsing safely
        if (typeof history === 'string') {
            try {
                this.currentHistory = JSON.parse(history);
            } catch (e) {
                this.currentHistory = [];
            }
        } else {
            this.currentHistory = history || [];
        }
        
        this.showHistory = true;
    },
    formatDate(dateStr) {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleString();
    },
    onTypeChange() {
      if (this.selectedIndex !== null && this.roles[this.selectedIndex]) {
          this.roles[this.selectedIndex].user_group_name = "";
          this.roles[this.selectedIndex].user_id = "";
      }
    },
    switchLanguage(lang) {
      this.$i18n.locale = lang;
    },
    navigateToAddPlaylist() {
      this.$router.push(`/add-playlist`);
    },
    navigateToEditPlaylist(id) {
      this.$router.push(`/edit-playlist/${id}`);
    },
    getAllPlaylist() {
      PlayListDataService.getAll()
        .then(response => {
          this.roles = response.data;
          console.log(this.roles);
        })
        .catch(e => {
          console.log(e);
        });
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
  },
};
</script>

<style scoped>
/* ==========================
   MODAL & HISTORY STYLES
========================== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
}

.modal-container {
  width: 100%;
  max-width: 850px; /* Slightly wider for better readability */
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}

.change-row {
  background: #fdfdfd;
  padding: 8px;
  border-radius: 8px;
  border-left: 3px solid #e9ecef;
}

.bg-light {
  background-color: #f1f3f5 !important;
}

.btn-close-custom {
  background: transparent;
  border: none;
  font-size: 28px;
  line-height: 1;
  color: #adb5bd;
  cursor: pointer;
  padding: 0 10px;
}
.btn-close-custom:hover {
  color: #343a40;
}

.bg-gray-100 { background-color: #f8f9fa !important; }
.badge-sm { font-size: 0.75rem; padding: 0.5em 0.8em; }

/* ==========================
   DYNAMIC THEME SYNC
========================== */
.btn-theme-primary {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: 1px solid var(--bs-primary) !important;
  font-weight: 800 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}
.btn-theme-primary:hover {
  filter: brightness(0.9);
  transform: translateY(-1px);
}

.btn-primary {
  background-color: var(--bs-primary) !important;
  border-color: var(--bs-primary) !important;
}

.btn-outline-primary {
  border: 1px solid var(--bs-primary) !important;
  color: var(--bs-primary) !important;
  background: transparent;
}
.btn-outline-primary:hover {
  background-color: var(--bs-primary) !important;
  color: white !important;
}

.theme-icon-bg {
  background-color: var(--bs-primary) !important;
}

/* ==========================
   PAGINATION SYNC
========================== */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  padding-bottom: 1rem;
}

.pagination button {
  background-color: var(--bs-primary) !important;
  color: white !important;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  margin: 0.5rem; 
  font-size: 12px;
  font-weight: 800 !important;
  text-transform: uppercase;
  cursor: pointer;
  transition: 0.2s;
}

.pagination button:disabled {
  background-color: #e2e8f0 !important;
  color: #94a3b8 !important;
  cursor: not-allowed;
}

.pagination button.active {
  background-color: #ffffff !important;
  color: var(--bs-primary) !important;
  border: 2px solid var(--bs-primary) !important;
}

.table th,
.table td {
  text-align: left;
  padding: 12px;
}

.fa-edit,
.fa-trash-alt,
.fa-history {
  font-size: 1rem;
}

.text-danger { color: #ea0606 !important; }
.text-info { color: #17a2b8 !important; }

@media (max-width: 768px) {
  .card-header {
    padding-bottom: 20px !important;
  }
  .modal-container {
    max-width: 95%;
  }
}
</style>